import { faEllipsisVertical, faXmark, faTrash, faCheck, faGear } from '@fortawesome/pro-regular-svg-icons';
import { faClockRotateLeft } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import { useRef, useState } from 'react';
import { createHttpClient } from '@adapters/HttpClient';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLinkItem,
  DropdownMenuTrigger,
} from '@shared/components/ui/dropdown-menu';
import { useToast } from '@shared/hooks/use-toast';
import { apiClientWH } from '@shared/services';
import { ShopsApiAdapter } from '../../adapters/ShopsApiAdapter';
import { CHANNELS_WITH_IMPORT } from '../../models/Shops';
import { DeleteShopConfirmModal, DeleteShopConfirmModalRef } from '../modals/DeleteShopConfirmModal';

interface DropdownActionsProps {
  shopId: number;
  shopStatus: number;
  channelId: number; // legacy, ya no se usa para eliminar
  onToggleStatus?: () => void;
  onDelete?: () => void;
  isMobile?: boolean;
  onOpenSettings?: () => void;
}

export const DropdownActions = ({
  shopId,
  shopStatus,
  channelId,
  onToggleStatus,
  onDelete,
  onOpenSettings,
}: DropdownActionsProps) => {
  const { t } = useTranslation('shopsModule');
  const { toast } = useToast();
  const isActive = shopStatus > 0;
  const confirmModalRef = useRef<DeleteShopConfirmModalRef>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isToggling, setIsToggling] = useState(false);

  // 🎯 Inicializar el adapter para eliminar tiendas
  const shopsAdapter = new ShopsApiAdapter(createHttpClient(apiClientWH));

  const handleToggleStatus = async () => {
    setIsToggling(true);

    try {
      const response = await shopsAdapter.toggleShopStatus(shopId);

      if (response.success) {
        toast({
          title: t('table.toggleSuccess.title'),
          description: response.message,
          variant: 'default',
        });

        // 🎯 Llamar al callback de cambio de estado exitoso para actualizar la lista
        onToggleStatus?.();
      } else {
        toast({
          title: t('table.toggleError.title'),
          description: response.message,
          variant: 'destructive',
        });
      }
    } catch {
      toast({
        title: t('table.toggleError.title'),
        description: t('table.toggleError.unexpected'),
        variant: 'destructive',
      });
    } finally {
      setIsToggling(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);

    try {
      const response = await shopsAdapter.deleteShop(shopId);

      if (response.success) {
        toast({
          title: t('table.deleteSuccess.title'),
          description: t('table.deleteSuccess.description'),
          variant: 'default',
        });

        // 🎯 Llamar al callback de eliminación exitosa para actualizar la lista
        onDelete?.();
      } else {
        toast({
          title: t('table.deleteError.title'),
          description: response.message,
          variant: 'destructive',
        });
      }
    } catch {
      toast({
        title: t('table.deleteError.title'),
        description: t('table.deleteError.unexpected'),
        variant: 'destructive',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteClick = () => {
    confirmModalRef.current?.open(handleDelete);
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="hover:bg-muted rounded-md h-8 w-8 px-3" data-testid="dropdown-trigger">
            <FontAwesomeIcon icon={faEllipsisVertical} className="text-icons" />
          </button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" className="w-44 border-card-border" data-testid="dropdown-content">
          {CHANNELS_WITH_IMPORT.includes(channelId) && (
            <DropdownMenuLinkItem
              to={`/shops/import-history/${shopId}`}
              data-testid="shop-history-menu-item"
              className="sm:hidden"
            >
              <FontAwesomeIcon icon={faClockRotateLeft} className="mr-2 w-3.5" />
              {t('buttons.history', 'Historial')}
            </DropdownMenuLinkItem>
          )}
          {/* Opción de configuración - solo visible en mobile si tiene permiso de editar */}
          {onOpenSettings && (
            <DropdownMenuItem onClick={onOpenSettings} className="sm:hidden">
              <FontAwesomeIcon icon={faGear} className="mr-2 w-3.5" />
              {t('table.settings')}
            </DropdownMenuItem>
          )}

          {/* Activar/Desactivar - solo si tiene permiso */}
          {onToggleStatus && (
            <DropdownMenuItem onClick={handleToggleStatus} disabled={isToggling} data-testid="toggle-status-button">
              <FontAwesomeIcon icon={isActive ? faXmark : faCheck} className="mr-2 w-3.5" />
              {isToggling ? t('table.toggling') : isActive ? t('table.deactivate') : t('table.activate')}
            </DropdownMenuItem>
          )}

          {/* Eliminar - solo si tiene permiso */}
          {onDelete && (
            <DropdownMenuItem
              onClick={handleDeleteClick}
              className="text-error"
              disabled={isDeleting}
              data-testid="delete-button"
            >
              <FontAwesomeIcon icon={faTrash} className="mr-2 w-3.5" />
              {isDeleting ? t('table.deleting') : t('table.delete')}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <DeleteShopConfirmModal ref={confirmModalRef} onConfirm={onDelete!} />
    </>
  );
};
